/**
 * useElementSelection Hook
 * Handles element selection and highlighting
 */

import { useEffect, useCallback, useRef } from 'react'
import { dia } from '@joint/core'
import { EditorTool } from '../../../map-editor-shared/store/uiStore'
import { computeFitFontSize, hugLabelBoxToText } from '../lib/labelTextMetrics'

interface UseElementSelectionReturn {
  handleElementClick: (elementId: string) => void
  handleBlankClick: () => void
}

/**
 * SVG path d 의 모든 (x, y) 좌표쌍을 scale.
 * 지원 명령어: M/m, L/l, T/t, S/s, Q/q, C/c — 좌표쌍 단위로 매칭하는 단순 정규식.
 * Z/z 는 매개변수 없음. 우리 도면의 path 는 대부분 M/L/Z 로 단순.
 */
function scalePathD(d: string, sx: number, sy: number): string {
  // 명령어 letter 와 number pair 둘 다 허용. 좌표 pair 단위로 substitute.
  return d.replace(/(-?\d+(?:\.\d+)?)(\s*,?\s*)(-?\d+(?:\.\d+)?)/g, (_, x, sep, y) => {
    return `${parseFloat(x) * sx}${sep}${parseFloat(y) * sy}`
  })
}

/** "x1,y1 x2,y2 ..." 또는 "x1,y1\nx2,y2" 형태의 점쌍 문자열을 scale. */
function scalePointPairs(input: string, sx: number, sy: number): string {
  return input.replace(/(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)/g, (_, x, y) => {
    return `${parseFloat(x) * sx},${parseFloat(y) * sy}`
  })
}

export function useElementSelection(
  graph: dia.Graph | null,
  paper: dia.Paper | null,
  selectedElementId: string | null,
  onSelectionChange: (elementId: string | null) => void,
  onClearMultiSelection?: () => void,
  hasMultiSelection?: boolean,
  currentTool: EditorTool = null
): UseElementSelectionReturn {
  // Canvas click selection is handled entirely by useCanvasClickSelection (native pointer events).
  // No JointJS element:pointerclick listener here — avoids duplicate state updates.

  const prevSelectedIdRef = useRef<string | null>(null)
  const overlayRef = useRef<HTMLDivElement | null>(null)

  // Remove the selection overlay if it exists
  const removeOverlay = useCallback(() => {
    if (overlayRef.current) {
      overlayRef.current.remove()
      overlayRef.current = null
    }
  }, [])

  // Create a DOM overlay to show selection highlight when SVG view is unavailable
  // (e.g., in hybrid rendering where static elements are on Canvas2D)
  // 8 resize handles 도 함께 부착 (4 corners + 4 edge midpoints).
  const showOverlay = useCallback((element: dia.Element) => {
    if (!paper) return
    removeOverlay()

    const bbox = element.getBBox()
    const scale = paper.scale().sx
    const translate = paper.translate()

    // Convert model coordinates to screen (pixel) coordinates
    const screenX = bbox.x * scale + translate.tx
    const screenY = bbox.y * scale + translate.ty
    const screenW = bbox.width * scale
    const screenH = bbox.height * scale

    const overlay = document.createElement('div')
    overlay.style.position = 'absolute'
    overlay.style.left = `${screenX}px`
    overlay.style.top = `${screenY}px`
    overlay.style.width = `${screenW}px`
    overlay.style.height = `${screenH}px`
    // outline 제거 — element 자체의 single-select-highlight (CSS outline) 가 selection 표시 역할.
    // overlay 는 핸들 8개의 위치 컨테이너 역할만 함.
    // outline 두 개 (polygon stroke + bbox outline) 가 따로 보이는 시각 중복 방지.
    // overlay 본체는 pointer 이벤트 무시 — 핸들만 잡도록
    overlay.style.pointerEvents = 'none'
    overlay.style.zIndex = '50'
    overlay.dataset.selectionOverlay = 'true'

    // ── Resize handles ────────────────────────────────────────────────
    // 8 handle: nw, n, ne, e, se, s, sw, w
    // 각 handle 은 overlay 기준 절대 위치, 고정 8x8 픽셀, 흰 배경 + 파란 테두리
    type HandleKind = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w'
    const HANDLE_SIZE = 8
    const HANDLE_HALF = HANDLE_SIZE / 2
    const handles: { kind: HandleKind; cursor: string; cx: string; cy: string }[] = [
      { kind: 'nw', cursor: 'nwse-resize', cx: '0%', cy: '0%' },
      { kind: 'n',  cursor: 'ns-resize',   cx: '50%', cy: '0%' },
      { kind: 'ne', cursor: 'nesw-resize', cx: '100%', cy: '0%' },
      { kind: 'e',  cursor: 'ew-resize',   cx: '100%', cy: '50%' },
      { kind: 'se', cursor: 'nwse-resize', cx: '100%', cy: '100%' },
      { kind: 's',  cursor: 'ns-resize',   cx: '50%', cy: '100%' },
      { kind: 'sw', cursor: 'nesw-resize', cx: '0%', cy: '100%' },
      { kind: 'w',  cursor: 'ew-resize',   cx: '0%', cy: '50%' },
    ]

    const MIN_W = 4 // world units
    const MIN_H = 4

    handles.forEach(({ kind, cursor, cx, cy }) => {
      const h = document.createElement('div')
      h.dataset.resizeHandle = kind
      h.style.position = 'absolute'
      h.style.width = `${HANDLE_SIZE}px`
      h.style.height = `${HANDLE_SIZE}px`
      h.style.left = `calc(${cx} - ${HANDLE_HALF}px)`
      h.style.top = `calc(${cy} - ${HANDLE_HALF}px)`
      h.style.background = '#ffffff'
      h.style.border = '1.5px solid #3B82F6'
      h.style.borderRadius = '2px'
      h.style.cursor = cursor
      h.style.pointerEvents = 'auto'
      h.style.boxSizing = 'border-box'

      h.addEventListener('pointerdown', (downEvt: PointerEvent) => {
        if (!paper) return
        downEvt.preventDefault()
        downEvt.stopPropagation()

        // 시작 시점의 element bbox + 마우스 위치 기록
        const startBox = element.getBBox()
        const startScale = paper.scale().sx
        const startMouseX = downEvt.clientX
        const startMouseY = downEvt.clientY

        // body 가 절대좌표 기반인 element 들 (standard.Path 의 d, standard.Polygon 의
        // refPoints 등) 은 element.resize() 만으로는 시각적으로 안 늘어남.
        // 시작 시점의 body/d 와 body/refPoints/points 와 vertices(data) 를 저장 후
        // 매 move 마다 비례 scale 해서 다시 set.
        const elementType = element.get('type')
        const startD = element.attr('body/d')
        const startRefPoints = element.attr('body/refPoints')
        const startPoints = element.attr('body/points')
        // data.vertices (export 시 사용되는 절대 좌표) 도 같이 scale 해야 export 가 정확.
        const startData = element.get('data')
        const startVertices = startData?.vertices as Array<[number, number]> | undefined
        const startOriginalPosition = startData?.originalPosition as { x: number; y: number } | undefined

        // 텍스트 라벨은 박스를 직접 늘리는 대신, 드래그한 박스 크기에 맞춰
        // fontSize 를 키우거나 줄인다(라벨창에 맞춘 폰트 자동 스케일).
        const isLabel = startData?.kind === 'label'
        const labelText = isLabel ? ((element.attr('label/text') as string) ?? '') : ''
        const labelFontFamily = isLabel
          ? ((element.attr('label/fontFamily') as string) || undefined)
          : undefined

        const onMove = (mvEvt: PointerEvent) => {
          // 화면 픽셀 → 월드 단위
          const dxWorld = (mvEvt.clientX - startMouseX) / startScale
          const dyWorld = (mvEvt.clientY - startMouseY) / startScale

          let newX = startBox.x
          let newY = startBox.y
          let newW = startBox.width
          let newH = startBox.height

          // 핸들 종류에 따라 어느 변/꼭짓점을 끄는지 결정
          if (kind.includes('w')) {
            newX = startBox.x + dxWorld
            newW = startBox.width - dxWorld
          }
          if (kind.includes('e')) {
            newW = startBox.width + dxWorld
          }
          if (kind.includes('n')) {
            newY = startBox.y + dyWorld
            newH = startBox.height - dyWorld
          }
          if (kind.includes('s')) {
            newH = startBox.height + dyWorld
          }

          // 최소 크기 보장
          if (newW < MIN_W) {
            // 좌측 핸들로 줄였는데 too small → x 도 조정해서 right edge 유지
            if (kind.includes('w')) newX -= MIN_W - newW
            newW = MIN_W
          }
          if (newH < MIN_H) {
            if (kind.includes('n')) newY -= MIN_H - newH
            newH = MIN_H
          }

          // 라벨: 드래그한 박스에 맞는 fontSize 로 환산 → box 는 글자에 맞춰
          // 다시 파생. fontSize 만 바뀌므로 저장/재로드에도 유지된다.
          if (isLabel) {
            const newFont = computeFitFontSize(labelText, newW, newH, labelFontFamily)
            element.attr('label/fontSize', newFont)
            element.position(newX, newY)
            element.resize(newW, newH)
            hugLabelBoxToText(element)
            return
          }

          // 비례 스케일 (원래 0×0 가드)
          const sx = startBox.width > 0 ? newW / startBox.width : 1
          const sy = startBox.height > 0 ? newH / startBox.height : 1

          element.position(newX, newY)
          element.resize(newW, newH)

          // body 가 절대 좌표인 경우 함께 scale.
          // standard.Path: d 의 좌표쌍 모두 scale (M/L/C/Q ... 명령어 외의 number pair).
          if (typeof startD === 'string' && startD.length > 0) {
            const newD = scalePathD(startD, sx, sy)
            element.attr('body/d', newD)
          }
          // standard.Polygon refPoints: 좌표쌍 scale
          if (typeof startRefPoints === 'string' && startRefPoints.length > 0) {
            const newRefPoints = scalePointPairs(startRefPoints, sx, sy)
            element.attr('body/refPoints', newRefPoints)
          }
          // standard.Polygon points (refPoints 안 쓰는 경우)
          if (typeof startPoints === 'string' && startPoints.length > 0) {
            const newPoints = scalePointPairs(startPoints, sx, sy)
            element.attr('body/points', newPoints)
          }

          // data.vertices 도 비례 scale — export/save 정확성 위해.
          // vertices 는 월드 좌표 절대값. 새 position 의 originalPosition 기준으로 변환.
          if (startVertices && startVertices.length > 0 && startOriginalPosition) {
            // 원래 vertex 가 startOriginalPosition 에서 (vx-opx, vy-opy) 만큼 떨어진
            // 위치였다면, scale 후엔 newX 에서 (vx-opx)*sx 만큼 떨어진 위치.
            const newVertices: Array<[number, number]> = startVertices.map(([vx, vy]) => [
              newX + (vx - startOriginalPosition.x) * sx,
              newY + (vy - startOriginalPosition.y) * sy,
            ])
            const data = element.get('data')
            element.set('data', {
              ...data,
              vertices: newVertices,
              originalPosition: { x: newX, y: newY },
            })
          } else if (elementType === 'standard.Path' || elementType === 'standard.Polygon') {
            // vertices 가 없어도 originalPosition 은 새 위치로 갱신 (drag 처리 호환)
            const data = element.get('data')
            if (data) {
              element.set('data', {
                ...data,
                originalPosition: { x: newX, y: newY },
              })
            }
          }
        }

        const onUp = () => {
          document.removeEventListener('pointermove', onMove)
          document.removeEventListener('pointerup', onUp)
        }
        document.addEventListener('pointermove', onMove)
        document.addEventListener('pointerup', onUp)
      })

      overlay.appendChild(h)
    })

    // Append to paper's parent (the canvas container)
    const container = (paper.el as HTMLElement).parentElement
    if (container) {
      container.appendChild(overlay)
      overlayRef.current = overlay
    }
  }, [paper, removeOverlay])

  // Update overlay position when paper transform changes (pan/zoom) OR
  // when the element itself moves/resizes (drag handles, property editor input).
  useEffect(() => {
    if (!paper || !graph || !selectedElementId) return

    const cell = graph.getCell(selectedElementId)
    if (!cell || !cell.isElement()) return
    const element = cell as dia.Element

    const updateOverlayPosition = () => {
      if (!overlayRef.current) return
      const bbox = element.getBBox()
      const scale = paper.scale().sx
      const translate = paper.translate()

      overlayRef.current.style.left = `${bbox.x * scale + translate.tx}px`
      overlayRef.current.style.top = `${bbox.y * scale + translate.ty}px`
      overlayRef.current.style.width = `${bbox.width * scale}px`
      overlayRef.current.style.height = `${bbox.height * scale}px`
    }

    paper.on('scale', updateOverlayPosition)
    paper.on('translate', updateOverlayPosition)
    // 요소 변경 (resize handle drag, property editor 입력 등) 시에도 overlay 갱신
    element.on('change:position change:size', updateOverlayPosition)

    return () => {
      paper.off('scale', updateOverlayPosition)
      paper.off('translate', updateOverlayPosition)
      element.off('change:position change:size', updateOverlayPosition)
    }
  }, [paper, graph, selectedElementId])

  // Update highlighting and interactivity — O(1) by tracking prev/next element
  useEffect(() => {
    if (!graph || !paper) return
    if (hasMultiSelection) return

    const prevId = prevSelectedIdRef.current
    prevSelectedIdRef.current = selectedElementId

    // Remove highlight from previous element
    if (prevId && prevId !== selectedElementId) {
      removeOverlay()
      const prevCell = graph.getCell(prevId)
      if (prevCell && prevCell.isElement()) {
        const view = paper.findViewByModel(prevCell)
        if (view && view.el) {
          view.el.classList.remove('single-select-highlight')
          view.el.style.removeProperty('outline-offset')
          const originalZ = prevCell.get('singleSelectOriginalZ')
          if (originalZ !== undefined) {
            prevCell.set('z', originalZ)
            prevCell.unset('singleSelectOriginalZ')
          }
        }
        const prevCellView = paper.findViewByModel(prevCell)
        if (prevCellView) prevCellView.setInteractivity(false)
      }
    }

    // Add highlight to new element
    if (selectedElementId) {
      const newCell = graph.getCell(selectedElementId)
      if (newCell && newCell.isElement()) {
        const view = paper.findViewByModel(newCell)
        const element = newCell as dia.Element
        if (view && view.el) {
          // SVG view exists — use CSS highlight (blue outline) for visual feedback
          view.el.classList.add('single-select-highlight')
          const size = element.size()
          const minDimension = Math.min(size.width, size.height)
          const offset = Math.max(0.5, Math.min(4, minDimension * 0.015))
          view.el.style.setProperty('outline-offset', `${offset}px`)

          const currentZ = newCell.get('z')
          if (newCell.get('singleSelectOriginalZ') === undefined) {
            newCell.set('singleSelectOriginalZ', currentZ)
          }
          newCell.toFront()
          const newCellView = paper.findViewByModel(newCell)
          // 선택돼도 드래그 이동은 막는다 — 위치는 도면 좌표 기준으로 고정.
          if (newCellView) newCellView.setInteractivity({ elementMove: false })
        }
        // overlay (with resize handles) 는 SVG 유무와 관계없이 항상 표시.
        // 핸들 자체는 pointer-events: auto, overlay 본체는 none 이라 element 클릭에도 영향 없음.
        showOverlay(element)
      }
    } else {
      // Deselected — remove overlay
      removeOverlay()
    }
  }, [graph, paper, selectedElementId, hasMultiSelection, removeOverlay, showOverlay])

  const handleElementClick = useCallback(
    (elementId: string) => {
      if (!graph || !paper) return

      onSelectionChange(elementId)

      // Find and center on element
      const element = graph.getCell(elementId)
      if (element && element.isElement()) {
        const bbox = element.getBBox()
        const currentScale = paper.scale()
        const viewportWidth = paper.el.parentElement?.clientWidth || 800
        const viewportHeight = paper.el.parentElement?.clientHeight || 600


        // Calculate center position
        const centerX = -bbox.x * currentScale.sx + viewportWidth / 2 - (bbox.width * currentScale.sx) / 2
        const centerY = -bbox.y * currentScale.sy + viewportHeight / 2 - (bbox.height * currentScale.sy) / 2

        paper.translate(centerX, centerY)
      }
    },
    [graph, paper, onSelectionChange]
  )

  const handleBlankClick = useCallback(() => {
    onSelectionChange(null)
    removeOverlay()

    // Clear multi-selection
    if (onClearMultiSelection) {
      onClearMultiSelection()
    }

    if (!graph || !paper) return

    // Remove all highlights and restore z-index
    graph.getCells().forEach((cell) => {
      if (cell.isElement()) {
        const view = paper.findViewByModel(cell)
        if (view && view.el) {
          view.el.classList.remove('single-select-highlight')
          view.el.classList.remove('multi-select-highlight')
          view.el.classList.remove('object-hover-highlight')
          view.el.style.removeProperty('outline-offset')

          const originalZ = cell.get('singleSelectOriginalZ')
          if (originalZ !== undefined) {
            cell.set('z', originalZ)
            cell.unset('singleSelectOriginalZ')
          }

          view.unhighlight()
        }
      }
    })
  }, [graph, paper, onSelectionChange, onClearMultiSelection, removeOverlay])

  return { handleElementClick, handleBlankClick }
}
